import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { getUser } from "../lib/auth";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { usePersistedPage } from "../lib/usePersistedPage";
import { useSortable } from "../lib/useSortable";

type BpRow = {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string | null;
  sector: string | null;
  stage: string | null;
  pitch: string;
  attachment_url: string | null;
  handled: boolean;
  created_at: string;
};

const FILTERS = [
  { v: "",      label: "全部" },
  { v: "false", label: "未处理" },
  { v: "true",  label: "已处理" },
] as const;

export function BpListPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<string>("");
  const [rows, setRows] = useState<BpRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = usePersistedPage("kexi_bp_page");
  const [delFor, setDelFor] = useState<BpRow | null>(null);
  const isAdmin = getUser()?.role === "admin";
  const { containerRef, pageSize } = useAutoPageSize();

  const load = () => {
    setError(null);
    setRows(null);
    const qs = filter ? `?handled=${filter}` : "";
    api.get<BpRow[]>(`/api/admin/bp${qs}`).then(setRows).catch((e) => setError(String(e)));
  };

  const doDelete = async () => {
    if (!delFor) return;
    try { await api.delete(`/api/admin/bp/${delFor.id}`); setRows((rs) => rs?.filter((x) => x.id !== delFor.id) ?? null); }
    catch (e) { setError(String(e)); }
    setDelFor(null);
  };

  useEffect(load, [filter]);
  // 切换筛选回第 1 页：在筛选按钮 onClick 里 setPage(1)（不用监听 effect，StrictMode 下不可靠）

  const { sorted, sortKey, sortDir, toggle } = useSortable(rows ?? []);
  const pageRows = rows ? sorted.slice((page - 1) * pageSize, page * pageSize) : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">BP 通道</div>
        <div className="page-actions">
          {FILTERS.map((f) => (
            <button key={f.v} className={`btn ${filter === f.v ? "btn-primary" : ""}`}
              style={{ height: 30 }} onClick={() => { setFilter(f.v); setPage(1); }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {!rows && !error && <div className="muted">加载中…</div>}
      {rows && rows.length === 0 && <div className="muted">暂无 BP 提交</div>}

      <div ref={containerRef}>
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
              <SortableTh sortKey="created_at" {...s} style={{ width: 100 }}>时间</SortableTh>
              <SortableTh sortKey="name" {...s} style={{ width: 90 }}>创始人</SortableTh>
              <SortableTh sortKey="company" {...s} style={{ width: 150 }}>公司</SortableTh>
              <SortableTh sortKey="sector" {...s} style={{ width: 140 }}>赛道</SortableTh>
              <SortableTh sortKey="stage" {...s} style={{ width: 90 }}>阶段</SortableTh>
              <SortableTh sortKey="pitch" {...s}>一句话事业</SortableTh>
              <SortableTh sortKey="handled" {...s} style={{ width: 80 }}>状态</SortableTh>
              <th style={{ width: 70 }}></th>
              </>; })()}
            </tr>
          </thead>
          <tbody>
            {pageRows!.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => nav(`/bp/${r.id}`)}>
                <td className="muted" title={new Date(r.created_at).toLocaleString()}>
                  {new Date(r.created_at).toLocaleDateString("zh-CN")}
                </td>
                <td>{r.name}</td>
                <td>{r.company}</td>
                <td>{r.sector || <span className="muted">—</span>}</td>
                <td>{r.stage || <span className="muted">—</span>}</td>
                <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.pitch}
                </td>
                <td>{r.handled ? <span className="muted">已处理</span> : <span className="success">●</span>}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {isAdmin && <button className="btn btn-xs" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => setDelFor(r)}>删除</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows && <Pager page={page} pageSize={pageSize} total={rows.length} onChange={setPage} />}
      </div>

      <ConfirmDialog
        open={delFor !== null}
        title="删除 BP 提交"
        message={delFor ? <>确认删除「{delFor.company}」的 BP 提交？记录与附件文件将被永久删除，不可恢复。</> : null}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setDelFor(null)}
      />
    </div>
  );
}
